import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { OrderLine, CustomLine, SavedItemsMenu, newLine, newCustomLine, filledCands, lineQty, lineTotal } from "@/components/OrderLines";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Plus, Search, Send, CheckCircle2 } from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const gbp = (v) => `£${Number(v || 0).toFixed(2)}`;

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const load = () => api.get("/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);
  if (!orders) return <Loading />;
  const s = q.trim().toLowerCase();
  const visible = orders.filter((o) => !s || [o.nursery_name, o.contact_name, o.contact_email, o.course_title, `ord-${o.number}`]
    .some((v) => (v || "").toLowerCase().includes(s)));
  return (
    <div data-testid="orders-page">
      <PageHeader title="Orders & Invoices" subtitle="Raise an invoice for a setting and book their candidates onto a session." testid="orders-header">
        <Button onClick={() => setOpen(true)} data-testid="new-order-button"><Plus className="h-4 w-4 mr-1.5" />New order</Button>
      </PageHeader>
      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search nursery, contact, course or ORD-number..." className="pl-9" data-testid="orders-search-input" />
      </div>
      {visible.length === 0 ? <EmptyState message="No orders yet." icon={FileText} /> : <OrdersTable orders={visible} reload={load} />}
      {open && <NewOrderDialog onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}

function OrdersTable({ orders, reload }) {
  const resend = async (o) => {
    try { await api.post(`/orders/${o.id}/resend`); toast.success(`Invoice re-sent to ${o.contact_email}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const markPaid = async (o) => {
    if (!window.confirm(`Mark ORD-${o.number} as paid?`)) return;
    await api.post(`/orders/${o.id}/mark-paid`);
    toast.success("Marked as paid");
    reload();
  };
  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Nursery</TableHead><TableHead>Course & session</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
              <TableCell><p className="font-mono font-medium">ORD-{o.number}</p><p className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</p></TableCell>
              <TableCell><p className="font-medium">{o.nursery_name}</p><p className="text-xs text-muted-foreground">{o.contact_name} · {o.contact_email}</p></TableCell>
              <TableCell className="max-w-xs"><p className="truncate" title={(o.lines || []).map((l) => l.course_title).join(", ")}>{o.course_title}</p><p className="text-xs text-muted-foreground">{o.lines?.length > 1 ? `${o.lines.length} items` : o.lines?.[0]?.kind === "custom" ? "Custom item" : o.session ? `${fmtDate(o.session.start)} ${fmtTime(o.session.start)}` : "E-learning"}{(o.places ?? o.quantity) ? ` · ${o.places ?? o.quantity} candidate(s)` : ""}</p></TableCell>
              <TableCell className="font-medium">{gbp(o.total)}</TableCell>
              <TableCell>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${o.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`order-status-${o.id}`}>{o.status === "paid" ? "Paid" : "Unpaid"}</span>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <a href={`${BACKEND}/api/orders/invoice/${o.pay_token}`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" data-testid={`order-invoice-${o.id}`}><FileText className="h-4 w-4 mr-1" />PDF</Button></a>
                <Button variant="ghost" size="sm" onClick={() => resend(o)} data-testid={`order-resend-${o.id}`}><Send className="h-4 w-4 mr-1" />Resend</Button>
                {o.status !== "paid" && <Button variant="outline" size="sm" onClick={() => markPaid(o)} data-testid={`order-mark-paid-${o.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Mark paid</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

const emptyForm = { nursery_name: "", nursery_address: "", contact_name: "", contact_email: "" };

function NewOrderDialog({ onClose, onSaved }) {
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [lines, setLines] = useState([newLine()]);
  const [sendCandidate, setSendCandidate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const loadItems = () => api.get("/invoice-items").then(({ data }) => setSavedItems(data)).catch(() => {});
  useEffect(() => { loadItems(); }, []);
  const pickItem = (it) => {
    const ln = { ...newCustomLine(), description: it.description, quantity: String(it.quantity), price_mode: it.price_mode,
      unit_price: it.price_mode === "each" ? String(it.price) : "", bulk_total: it.price_mode === "bulk" ? String(it.price) : "" };
    const blank = lines.length === 1 && lines[0].kind !== "custom" && !lines[0].course_id && !filledCands(lines[0]).length;
    setLines(blank ? [ln] : [...lines, ln]);
  };
  const saveItem = async (ln) => {
    const price = Number(ln.price_mode === "bulk" ? ln.bulk_total : ln.unit_price);
    if (!ln.description.trim() || !(price > 0)) return toast.error("Add a description and price first");
    try {
      const { data } = await api.post("/invoice-items", { description: ln.description.trim(), quantity: lineQty(ln), price_mode: ln.price_mode, price });
      toast.success(data.updated ? `Updated saved item "${data.description}"` : `Saved "${data.description}" for next time`);
      loadItems();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteItem = async (it) => {
    if (!window.confirm(`Delete saved item "${it.description}"?`)) return;
    await api.delete(`/invoice-items/${it.id}`).catch(() => {});
    loadItems();
  };
  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data.filter((c) => c.billing !== "monthly")));
    api.get("/sessions").then(({ data }) => setSessions(data));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLive = (ln) => courses.find((x) => x.id === ln.course_id)?.course_type === "live";
  const subtotal = lines.reduce((t, ln) => t + lineTotal(ln), 0);
  const vat = Math.round(subtotal * 20) / 100;
  const setLine = (i, ln) => setLines(lines.map((x, j) => (j === i ? ln : x)));

  const save = async () => {
    if (["nursery_name", "nursery_address", "contact_name", "contact_email"].some((k) => !form[k].trim())) return toast.error("Please complete the nursery and contact details");
    for (const [i, ln] of lines.entries()) {
      const priced = ln.price_mode === "bulk" ? Number(ln.bulk_total) > 0 : Number(ln.unit_price) > 0;
      if (ln.kind === "custom") {
        if (!ln.description.trim() || !priced) return toast.error(`Line ${i + 1}: add a description and price`);
        continue;
      }
      if (!ln.course_id || (isLive(ln) && !ln.session_id) || !priced) return toast.error(`Line ${i + 1}: choose the course${isLive(ln) ? ", session" : ""} and price`);
      const f = filledCands(ln);
      if (f.length === 0 || f.some((c) => !c.name.trim() || !c.email.trim())) return toast.error(`Line ${i + 1}: every candidate needs a name and email`);
    }
    setSaving(true);
    try {
      const { data } = await api.post("/orders", {
        nursery_name: form.nursery_name, nursery_address: form.nursery_address, contact_name: form.contact_name,
        contact_email: form.contact_email, send_candidate_emails: sendCandidate,
        lines: lines.map((ln) => {
          const price = ln.price_mode === "bulk" ? { line_total: Number(ln.bulk_total) } : { unit_price: Number(ln.unit_price) };
          return ln.kind === "custom"
            ? { kind: "custom", description: ln.description.trim(), quantity: lineQty(ln), ...price }
            : { kind: "course", course_id: ln.course_id, session_id: isLive(ln) ? ln.session_id : null, candidates: filledCands(ln), ...price };
        }),
      });
      toast.success(`Invoice ORD-${data.number} sent to ${data.contact_email}${data.places ? ` · ${data.places} place(s) booked` : ""}`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-order-dialog">
        <DialogHeader><DialogTitle className="font-heading">New order</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nursery name</Label><Input value={form.nursery_name} onChange={(e) => set("nursery_name", e.target.value)} className="mt-1.5" data-testid="order-nursery-name-input" /></div>
            <div><Label>Order contact name</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5" data-testid="order-contact-name-input" /></div>
            <div><Label>Nursery address</Label><Textarea value={form.nursery_address} onChange={(e) => set("nursery_address", e.target.value)} rows={3} className="mt-1.5" data-testid="order-nursery-address-input" /></div>
            <div><Label>Order contact email (invoice goes here)</Label><Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5" data-testid="order-contact-email-input" /></div>
          </div>
          {lines.map((ln, i) => {
            const rm = lines.length > 1 ? () => setLines(lines.filter((_, j) => j !== i)) : null;
            return ln.kind === "custom"
              ? <CustomLine key={i} idx={i} line={ln} setLine={(x) => setLine(i, x)} onRemove={rm} onSaveItem={() => saveItem(ln)} />
              : <OrderLine key={i} idx={i} line={ln} setLine={(x) => setLine(i, x)} courses={courses} sessions={sessions} onRemove={rm} />;
          })}
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { const last = [...lines].reverse().find((l) => l.kind !== "custom"); setLines([...lines, newLine(last ? filledCands(last)[0]?.email || "" : "")]); }} data-testid="order-add-course-button"><Plus className="h-4 w-4 mr-1.5" />Add another course</Button>
            <Button variant="outline" onClick={() => setLines([...lines, newCustomLine()])} data-testid="order-add-custom-button"><Plus className="h-4 w-4 mr-1.5" />Add custom line</Button>
            <SavedItemsMenu items={savedItems} onPick={pickItem} onDelete={deleteItem} />
          </div>
          {lines.some((l) => l.kind !== "custom") && <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={sendCandidate} onCheckedChange={(v) => setSendCandidate(!!v)} data-testid="order-send-candidate-emails-checkbox" />
            Also email each candidate their course access link
          </label>}
          <div className="rounded-lg border border-border p-3 text-sm space-y-1 max-w-sm ml-auto" data-testid="order-totals">
            {lines.map((ln, i) => (
              <div key={i} className="flex justify-between gap-3"><span className="text-muted-foreground truncate">{ln.kind === "custom" ? ln.description || `Line ${i + 1}` : courses.find((c) => c.id === ln.course_id)?.title || `Course ${i + 1}`}: {ln.price_mode === "bulk" ? `${lineQty(ln)} · bulk` : `${lineQty(ln)} × ${gbp(ln.unit_price)}`}</span><span>{gbp(lineTotal(ln))}</span></div>
            ))}
            <div className="flex justify-between border-t border-border pt-1"><span className="text-muted-foreground">Subtotal (ex VAT)</span><span>{gbp(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">VAT (20%)</span><span>{gbp(vat)}</span></div>
            <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Total</span><span data-testid="order-total-amount">{gbp(subtotal + vat)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="order-submit-button">{saving ? "Sending..." : "Book & send invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
